// Tail-spend deal lifecycle — pure FSM.
//
// Two axes that the prototype confused into one badge:
//   1. Supplier relationship (Monitoring / Triggered / UnderContract / …)
//   2. Deal lifecycle (Triage / Negotiating / Escalated / …)
// We store both and derive the badge.

import { DecisionOptionKind, EscalationReason } from "./types";

export type Party = "supplier" | "agent";

export type ExecRouting =
    | "auto_execute"
    | "buyer_esign"
    | "escalate"
    | "hand_to_strategic";

export type DealState =
    | { kind: "Triage" }
    | { kind: "Negotiating"; round: number; awaiting: Party; manual?: boolean }
    | { kind: "AwaitingConfirmation"; round: number }
    | { kind: "Escalated"; reason: EscalationReason; resumeRound: number }
    | { kind: "Held"; reason: EscalationReason }
    | {
          kind: "Executing";
          routing: Extract<ExecRouting, "auto_execute" | "buyer_esign">;
      }
    | { kind: "ClosedWon" }
    | { kind: "Walked" }
    | { kind: "Reassigned" };

export type DealStateKind = DealState["kind"];

export type DealEvent =
    | { type: "OpenNegotiation"; annualValue: number; cap: number }
    | { type: "ReassignManually" }
    | { type: "SupplierCountered"; withinEnvelope: boolean }
    | { type: "SupplierAccepted" }
    | { type: "AgentAccepts" }
    | { type: "SendCounter"; ladderExhausted: boolean }
    | { type: "SupplierSilent" }
    | { type: "SupplierConfirmed"; routing: ExecRouting }
    | { type: "ExecutionCompleted" }
    | { type: "HumanDecision"; option: DecisionOptionKind };

export type Transition =
    | { ok: true; next: DealState }
    | { ok: false; reason: string };

const TERMINAL: ReadonlySet<DealStateKind> = new Set<DealStateKind>([
    "ClosedWon",
    "Walked",
    "Reassigned",
]);

export const isTerminal = (s: DealState): boolean => TERMINAL.has(s.kind);

function assertNever(x: never): never {
    throw new Error(`Unhandled case: ${JSON.stringify(x)}`);
}

export function transition(state: DealState, event: DealEvent): Transition {
    const invalid = (): Transition => ({
        ok: false,
        reason: `Event "${event.type}" is not valid in state "${state.kind}"`,
    });

    switch (state.kind) {
        case "Triage": {
            if (event.type === "OpenNegotiation") {
                return event.annualValue > event.cap
                    ? {
                          ok: true,
                          next: { kind: "Held", reason: "above_autonomous_cap" },
                      }
                    : {
                          ok: true,
                          next: {
                              kind: "Negotiating",
                              round: 1,
                              awaiting: "supplier",
                          },
                      };
            }
            if (event.type === "ReassignManually")
                return { ok: true, next: { kind: "Reassigned" } };
            return invalid();
        }
        case "Negotiating": {
            if (state.awaiting === "supplier") {
                switch (event.type) {
                    case "SupplierCountered":
                        return event.withinEnvelope
                            ? {
                                  ok: true,
                                  next: { ...state, awaiting: "agent" },
                              }
                            : {
                                  ok: true,
                                  next: {
                                      kind: "Escalated",
                                      reason: "floor_breach",
                                      resumeRound: state.round,
                                  },
                              };
                    case "SupplierAccepted":
                        return {
                            ok: true,
                            next: {
                                kind: "AwaitingConfirmation",
                                round: state.round,
                            },
                        };
                    case "SupplierSilent":
                        return {
                            ok: true,
                            next: {
                                kind: "Escalated",
                                reason: "supplier_silent",
                                resumeRound: state.round,
                            },
                        };
                    default:
                        return invalid();
                }
            } else {
                switch (event.type) {
                    case "SendCounter":
                        return event.ladderExhausted
                            ? {
                                  ok: true,
                                  next: {
                                      kind: "Escalated",
                                      reason: "ladder_exhausted",
                                      resumeRound: state.round,
                                  },
                              }
                            : {
                                  ok: true,
                                  next: {
                                      ...state,
                                      round: state.round + 1,
                                      awaiting: "supplier",
                                  },
                              };
                    case "AgentAccepts":
                        return {
                            ok: true,
                            next: {
                                kind: "AwaitingConfirmation",
                                round: state.round,
                            },
                        };
                    default:
                        return invalid();
                }
            }
        }
        case "AwaitingConfirmation": {
            if (event.type === "SupplierConfirmed") {
                switch (event.routing) {
                    case "auto_execute":
                    case "buyer_esign":
                        return {
                            ok: true,
                            next: {
                                kind: "Executing",
                                routing: event.routing,
                            },
                        };
                    case "escalate":
                        return {
                            ok: true,
                            next: {
                                kind: "Escalated",
                                reason: "esign_band",
                                resumeRound: state.round,
                            },
                        };
                    case "hand_to_strategic":
                        return { ok: true, next: { kind: "Reassigned" } };
                    default:
                        return assertNever(event.routing);
                }
            }
            return invalid();
        }
        case "Escalated": {
            if (event.type === "HumanDecision") {
                switch (event.option) {
                    case "adjust":
                    case "trade":
                    case "keep":
                        return {
                            ok: true,
                            next: {
                                kind: "Negotiating",
                                round: state.resumeRound,
                                awaiting: "agent",
                            },
                        };
                    case "take":
                        return {
                            ok: true,
                            next: {
                                kind: "Negotiating",
                                round: state.resumeRound,
                                awaiting: "agent",
                                manual: true,
                            },
                        };
                    case "walk":
                        return { ok: true, next: { kind: "Walked" } };
                    case "reassign":
                        return { ok: true, next: { kind: "Reassigned" } };
                    default:
                        return assertNever(event.option);
                }
            }
            return invalid();
        }
        case "Held": {
            if (event.type === "HumanDecision") {
                switch (event.option) {
                    case "reassign":
                        return { ok: true, next: { kind: "Reassigned" } };
                    case "keep":
                        return {
                            ok: true,
                            next: {
                                kind: "Negotiating",
                                round: 1,
                                awaiting: "supplier",
                            },
                        };
                    case "take":
                        return {
                            ok: true,
                            next: {
                                kind: "Negotiating",
                                round: 1,
                                awaiting: "supplier",
                                manual: true,
                            },
                        };
                    default:
                        return invalid();
                }
            }
            return invalid();
        }
        case "Executing": {
            if (event.type === "ExecutionCompleted")
                return { ok: true, next: { kind: "ClosedWon" } };
            return invalid();
        }
        case "ClosedWon":
        case "Walked":
        case "Reassigned":
            return invalid();
        default:
            return assertNever(state);
    }
}

// High-level stage name — the FSM kind, no sub-state. Used where the row
// already carries a separate status chip for the detail (round, who's turn,
// reason, routing, etc.).
export function stageKindLabel(state: DealState): string {
    switch (state.kind) {
        case "Triage":
            return "Triage";
        case "Negotiating":
            return "Negotiating";
        case "AwaitingConfirmation":
            return "Awaiting confirmation";
        case "Escalated":
            return "Escalated";
        case "Held":
            return "Held";
        case "Executing":
            return "Executing";
        case "ClosedWon":
            return "Closed";
        case "Walked":
            return "Walked";
        case "Reassigned":
            return "Reassigned";
        default:
            return assertNever(state);
    }
}

export function stageLabel(state: DealState): string {
    switch (state.kind) {
        case "Triage":
            return "Triage";
        case "Negotiating": {
            const who =
                state.awaiting === "supplier"
                    ? "awaiting supplier"
                    : "agent’s turn";
            const stage =
                state.round === 1 && state.awaiting === "supplier"
                    ? "Outreach"
                    : `Round ${state.round}`;
            return state.manual ? `${stage} · ${who} · manual` : `${stage} · ${who}`;
        }
        case "AwaitingConfirmation":
            return "Awaiting confirmation";
        case "Escalated":
            return "Paused · needs you";
        case "Held":
            return "Held · above cap";
        case "Executing":
            return state.routing === "buyer_esign"
                ? "Executing · buyer e-sign"
                : "Executing · auto";
        case "ClosedWon":
            return "Closed";
        case "Walked":
            return "Walked / re-sourced";
        case "Reassigned":
            return "Reassigned";
        default:
            return assertNever(state);
    }
}
